// Per-client Supervisor Visits view. supervisor_visits is keyed by client UUID.
//
// What is due comes from supervision_status (the same rules the scheduler
// uses): one line per supervised person — caregiver, LPN — plus the RN client
// review. Staff-supervision visits open the one-page short form and file on
// the agency template; the RN client review opens the comprehensive form.
// "Backfill history" creates one slot per interval from the start of service
// for the nurse to document.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { KIND_LABEL, SUBJECT_KINDS, scheduleSupervision, planSupervisionBackfill, runSupervisionBackfill, BACKFILL_SINCE } from '../../lib/supervision';
import SupervisionShortForm from '../clinical/SupervisionShortForm';
import useIsMobile from '../../hooks/useIsMobile';
import DataTable from '../DataTable';

const card = { background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
const field = { padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit' };
// Dates are DATE columns: parse as local noon so they don't shift a day back
// when rendered in Eastern time.
const fmt = (d) => d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US') : '—';
const today = () => new Date().toISOString().slice(0, 10);

export default function ClientSupervisorVisitsTab({ client }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [state, setState] = useState({ loading: true, rows: [] });
  const [due, setDue] = useState([]);
  const [open, setOpen] = useState(null);       // { supervisorVisitId } | { visitId }
  const [adhoc, setAdhoc] = useState(null);     // { kind, staffId }
  const [backfill, setBackfill] = useState(null); // { loading, pairs:[{kind, staffId, name, from, slots}] }
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const say = (m) => { setToast(m); setTimeout(() => setToast(''), 5000); };

  const load = useCallback(async () => {
    const [{ data: rows }, { data: d }] = await Promise.all([
      supabase.from('supervisor_visits').select('*, supervised:staff!supervisor_visits_supervised_staff_id_fkey(full_name, position_code)').eq('client_id', client.id).neq('status', 'archived').order('visit_date', { ascending: false }),
      supabase.from('supervision_status').select('*').eq('client_id', client.id),
    ]);
    setState({ loading: false, rows: rows || [] }); setDue(d || []);
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  // Anyone on the schedule for this client, for the ad-hoc and backfill pickers.
  const people = useMemo(() => due.filter(d => d.kind !== 'client').map(d => ({ kind: d.kind, staffId: d.staff_id, name: d.staff_full || 'Family caregiver', position: d.position_code })), [due]);

  // Short form for staff supervision; the comprehensive form for the RN client
  // review and for the July-era visits that were written on it.
  const isShort = (v) => v.form_kind === 'short' || (v.status !== 'complete' && v.subject_kind && v.subject_kind !== 'client');
  const openRow = (v) => {
    if (isShort(v)) setOpen({ supervisorVisitId: v.id });
    else navigate(`/hh/supervisor-visits/${v.id}`);
  };

  // Ad hoc: book a visit for today for the chosen person and open the form.
  const startAdhoc = async () => {
    if (!adhoc?.kind) return;
    setBusy('adhoc'); setError('');
    try {
      const id = await scheduleSupervision(adhoc.kind, client.id, adhoc.staffId || null, today());
      setAdhoc(null); setOpen({ supervisorVisitId: id }); load();
    } catch (e) {
      // Already scheduled for this pair: open that one instead.
      const existing = state.rows.find(r => r.status === 'scheduled' && r.subject_kind === adhoc.kind && (r.supervised_staff_id || null) === (adhoc.staffId || null));
      if (existing) { setAdhoc(null); setOpen({ supervisorVisitId: existing.id }); } else setError(e.message);
    } finally { setBusy(''); }
  };

  // Backfill: the start of service is the first unskilled visit in AxisCare.
  const planBackfill = async () => {
    setBackfill({ loading: true, pairs: [] }); setError('');
    try { setBackfill({ loading: false, pairs: await planSupervisionBackfill([client]), since: BACKFILL_SINCE }); }
    catch (e) { setError(e.message); setBackfill(null); }
  };
  const runBackfill = async () => {
    setBusy('backfill'); setError('');
    try {
      const n = await runSupervisionBackfill(backfill.pairs);
      say(`${n} supervisory visit slot${n === 1 ? '' : 's'} created for the nurse to document.`);
      setBackfill(null); load();
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };

  const columns = [
    { id: 'visit_date', header: 'Visit date', size: 120, enableSorting: false, meta: { mobile: 'primary' }, cell: info => fmt(info.row.original.visit_date) },
    { id: 'who', header: 'Supervision of', size: 220, enableSorting: false, meta: { mobile: 'primary' },
      cell: info => { const v = info.row.original; const who = v.subject_kind === 'client' || !v.subject_kind ? 'Client review (RN)' : (v.supervised?.full_name || v.supervised_family_name || 'Family caregiver');
        return <div><div style={{ fontSize: 12.5 }}>{who}</div><div style={{ fontSize: 11, color: 'var(--text3)' }}>{v.subject_kind ? KIND_LABEL[v.subject_kind] : ''}{v.backfill && v.status !== 'complete' ? ' · backfill' : ''}</div></div>; } },
    { id: 'status', header: 'Status', size: 130, enableSorting: false, meta: { mobile: 'primary' },
      cell: info => { const v = info.row.original; const s = v.status || 'draft';
        const l = s === 'scheduled' && v.backfill ? 'To document' : s === 'scheduled' ? 'Scheduled' : s === 'complete' ? 'Complete' : 'Draft';
        const c = s === 'complete' ? 'var(--success)' : v.backfill ? 'var(--warn)' : 'var(--text2)';
        return <span style={{ fontSize: 12, color: c, fontWeight: 500 }}>{l}</span>; } },
    { id: 'by', header: 'Nurse', size: 160, enableSorting: false, meta: { mobile: 'secondary' }, cell: info => { const v = info.row.original; return v.status === 'complete' ? `${v.nurse_signed_name || v.supervisor_name || '—'}${v.supervising_credential ? `, ${v.supervising_credential}` : ''}` : (v.supervisor_name || '—'); } },
    { id: 'actions', header: '', size: 170, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => { const v = info.row.original; const short = isShort(v);
        return (
          <div style={{ textAlign: 'right', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => openRow(v)}>{v.status === 'complete' ? 'View' : short && v.backfill ? 'Document' : 'Open'}</button>
            {v.status === 'complete' && (short
              ? (v.report_drive_url ? <a className="btn btn-outline btn-sm" style={{ fontSize: 11 }} href={v.report_drive_url} target="_blank" rel="noreferrer">Report ↗</a> : null)
              : <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--accent)', borderColor: 'var(--accent)' }} onClick={() => navigate(`/hh/supervisor-visits/${v.id}/report`)}>Report</button>)}
          </div>
        ); } },
  ];

  const anyOverdue = due.some(d => d.status === 'overdue');
  const pending = state.rows.filter(r => r.status === 'scheduled' && r.backfill).length;

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0 }}>Supervisor Visits</h3>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={planBackfill} disabled={!client.axiscare_client_id || busy === 'backfill'} title={client.axiscare_client_id ? 'Create a slot per interval from the first visit in AxisCare' : 'No AxisCare link on this client'}>Backfill history</button>
          <button className="btn btn-outline btn-sm" onClick={() => setAdhoc({ kind: 'aide', staffId: people.find(p => p.kind === 'aide')?.staffId || '' })}>+ Staff supervision</button>
          <button className="btn btn-sm" onClick={() => navigate('/hh/supervisor-visits/new')} style={{ background: 'var(--accent)', color: 'var(--white)', border: 'none', borderRadius: 6, padding: '7px 12px', cursor: 'pointer' }}>+ Client review</button>
        </div>
      </div>
      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)', marginTop: 10 }}>{error}</div>}
      {toast && <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--success)', marginTop: 10 }}>✓ {toast}</div>}

      <div style={{ marginTop: 10, marginBottom: 4, padding: '10px 12px', borderRadius: 8, fontSize: 12.5, background: anyOverdue ? 'var(--danger-bg)' : 'var(--bg2)', border: `1px solid ${anyOverdue ? 'var(--danger)' : 'var(--border)'}` }}>
        {due.length === 0 && <div style={{ color: 'var(--text3)' }}>Nobody to supervise yet — pairs come from this client's schedule (any caregiver with a visit in the last 30 or next 14 days).</div>}
        {due.map(d => (
          <div key={`${d.kind}|${d.staff_id || ''}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '4px 0', flexWrap: 'wrap' }}>
            <span><b>{d.kind === 'client' ? KIND_LABEL.client : `${d.staff_full || 'Family caregiver'}`}</b> <span style={{ color: 'var(--text3)' }}>· {d.kind === 'client' ? 'every 60 days' : `${KIND_LABEL[d.kind]} · every ${d.interval_days} days`}</span></span>
            <span style={{ color: d.status === 'overdue' ? 'var(--danger)' : d.status === 'due' ? 'var(--warn)' : 'var(--text3)' }}>
              {d.last_date ? `last ${fmt(d.last_date)}` : 'no completed visit yet'} · next due {fmt(d.next_due)}{d.status === 'overdue' ? ' — OVERDUE' : ''}{d.scheduled_start ? ` · scheduled ${fmt(d.scheduled_start)}` : ''}
            </span>
          </div>
        ))}
        {pending > 0 && <div style={{ marginTop: 6, color: 'var(--warn)' }}>{pending} backfilled slot{pending === 1 ? '' : 's'} waiting to be documented.</div>}
      </div>

      <div style={{ marginTop: 10 }}>
        <DataTable tableId="client-supervisor-visits" columns={columns} data={state.rows} getRowId={v => v.id} loading={state.loading}
          emptyMessage="No supervisor visits recorded for this client yet." showColumnPicker={false} maxHeight="none" />
      </div>

      {open && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setOpen(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: isMobile ? '100%' : 760 }}>
            <div className="modal-header"><span>Supervisory visit report</span><button className="btn btn-outline btn-sm" onClick={() => setOpen(null)}>Close</button></div>
            <div className="modal-body"><SupervisionShortForm {...open} onDone={(r) => { setOpen(null); say(r.report ? 'Signed, completed and filed to Drive.' : 'Signed and completed.'); load(); }} onCancel={() => setOpen(null)} /></div>
          </div>
        </div>
      )}

      {adhoc && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setAdhoc(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <div className="modal-header"><span>Staff supervision visit</span><button className="btn btn-outline btn-sm" onClick={() => setAdhoc(null)}>Close</button></div>
            <div className="modal-body">
              <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 10 }}>Books a supervisory visit for today and opens the form. Caregiver and LPN visits follow their cadence; RN supervision is as required.</div>
              <div style={{ display: 'grid', gap: 10 }}>
                <label style={{ fontSize: 12 }}>Who is being supervised
                  <select style={{ ...field, width: '100%', marginTop: 4 }} value={adhoc.kind} onChange={e => setAdhoc({ kind: e.target.value, staffId: people.find(p => p.kind === e.target.value)?.staffId || '' })}>
                    {SUBJECT_KINDS.map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                  </select></label>
                <label style={{ fontSize: 12 }}>Name
                  <select style={{ ...field, width: '100%', marginTop: 4 }} value={adhoc.staffId} onChange={e => setAdhoc({ ...adhoc, staffId: e.target.value })}>
                    <option value="">{adhoc.kind === 'aide' ? 'Family caregiver (not on roster)' : 'Choose…'}</option>
                    {people.filter(p => p.kind === adhoc.kind).map(p => <option key={p.staffId} value={p.staffId}>{p.name} · {p.position}</option>)}
                  </select></label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                <button className="btn btn-outline" onClick={() => setAdhoc(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={busy === 'adhoc' || (adhoc.kind !== 'aide' && !adhoc.staffId)} onClick={startAdhoc}>{busy === 'adhoc' ? 'Opening…' : 'Open form'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {backfill && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setBackfill(null)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header"><span>Backfill supervisory visit history</span><button className="btn btn-outline btn-sm" onClick={() => setBackfill(null)}>Close</button></div>
            <div className="modal-body">
              {backfill.loading ? <div style={{ color: 'var(--text3)', fontSize: 13 }}>Reading AxisCare visit history…</div> : (
                <>
                  <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 10 }}>Start of service is each caregiver's first visit in AxisCare (from {fmt(backfill.since)}). One slot per interval is created up to today — caregivers every 14 days, LPNs every 30 — for the nurse to document with the real date. Slots already covered by a visit are skipped.</div>
                  {backfill.pairs.length === 0 ? <div style={{ fontSize: 13, color: 'var(--text3)' }}>No caregiver or LPN visits found in AxisCare for this client.</div> : (
                    <table style={{ width: '100%', fontSize: 12.5, borderCollapse: 'collapse' }}>
                      <thead><tr>{['Supervised', 'Role', 'First visit', 'Slots'].map(h => <th key={h} style={{ textAlign: 'left', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', padding: '4px 6px', borderBottom: '1px solid var(--border)' }}>{h}</th>)}</tr></thead>
                      <tbody>{backfill.pairs.map(p => <tr key={p.staffId}><td style={{ padding: '5px 6px' }}>{p.name}</td><td style={{ padding: '5px 6px' }}>{KIND_LABEL[p.kind]}</td><td style={{ padding: '5px 6px' }}>{fmt(p.from)}</td><td style={{ padding: '5px 6px' }}>{p.slots}</td></tr>)}</tbody>
                    </table>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button className="btn btn-outline" onClick={() => setBackfill(null)}>Cancel</button>
                    <button className="btn btn-primary" disabled={busy === 'backfill' || backfill.pairs.length === 0} onClick={runBackfill}>{busy === 'backfill' ? 'Creating…' : 'Create slots'}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
